import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, inject } from '@angular/core';
import { ApiService } from '../../service/api.service';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AvatarComponent } from '../../helper/avatar/avatar.component';

export interface Supporter {
  vrchat_name: string;
  tier: number;
  color: string;
}

@Component({
  selector: 'app-credits',
  imports: [CommonModule, MatCardModule, AvatarComponent, MatProgressSpinnerModule],
  templateUrl: './credits.component.html',
  styleUrl: './credits.component.less'
})
export class CreditsComponent implements OnInit, OnDestroy, AfterViewInit {
  api: ApiService = inject(ApiService);
  supporters: Supporter[] = [];
  loading = true;
  loadingMore = false;
  hasMore = true;
  offset = 0;
  limit = 50;
  private intersectionObserver?: IntersectionObserver;
  @ViewChild('loadMoreSentinel') loadMoreSentinel!: ElementRef<HTMLElement>;
  @ViewChild('supportersContainer') supportersContainer!: ElementRef<HTMLElement>;

  ngOnInit() {
    this.loadSupporters(true);
  }

  ngAfterViewInit() {
    // Observer will be set up after initial data loads
  }

  ngOnDestroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  setupIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    // Use setTimeout to ensure the view has updated and elements are available
    setTimeout(() => {
      if (!this.loadMoreSentinel?.nativeElement || !this.supportersContainer?.nativeElement) {
        return;
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && this.hasMore && !this.loadingMore && !this.loading) {
              this.loadMore();
            }
          });
        },
        {
          root: this.supportersContainer.nativeElement, // Use the scrollable container as root
          rootMargin: '50px',
          threshold: 0.1
        }
      );

      this.intersectionObserver.observe(this.loadMoreSentinel.nativeElement);
    }, 100);
  }

  loadSupporters(reset: boolean = false) {
    if (reset) {
      this.supporters = [];
      this.offset = 0;
      this.hasMore = true;
      this.loading = true;
    } else {
      this.loadingMore = true;
    }

    const url = `credits/getSupporters.php?offset=${this.offset}&limit=${this.limit}`;
    this.api.get(url).subscribe((data: any) => {
      if (data.code === 200) {
        const newSupporters = data.supporters || [];
        if (reset) {
          this.supporters = newSupporters;
        } else {
          this.supporters = [...this.supporters, ...newSupporters];
        }
        this.hasMore = data.has_more ?? true;
        this.offset += newSupporters.length;
      }
      this.loading = false;
      this.loadingMore = false;
      
      // Set up or re-observe after data loads
      setTimeout(() => {
        if (reset || !this.intersectionObserver) {
          this.setupIntersectionObserver();
        } else if (this.loadMoreSentinel?.nativeElement) {
          // Re-observe the sentinel after new data is added
          this.intersectionObserver?.observe(this.loadMoreSentinel.nativeElement);
        }
      }, 100);
    });
  }

  loadMore() {
    if (!this.loadingMore && this.hasMore) {
      this.loadSupporters(false);
    }
  }
}
